import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import api from '../../utils/axiosConfig';
import {
  Ticket,
  TicketActivity,
  TicketAttachment,
  TicketCategory,
  TicketComment,
  TicketPriority,
  TicketsState,
  TicketStatus
} from '../../types';

// Initial state
const initialState: TicketsState = {
  tickets: [],
  selectedTicket: null,
  comments: [],
  attachments: [],
  activity: [],
  isLoading: false,
  isDetailsLoading: false,
  error: null,
};

// Helper function to set auth header
const setAuthHeader = () => {
  const token = localStorage.getItem('token');
  if (token) {
    // Token is automatically added by the api instance
  }
};

// Async thunks
export const fetchTickets = createAsyncThunk(
  'tickets/fetchTickets',
  async (filters: {
    status?: TicketStatus;
    priority?: TicketPriority;
    category?: TicketCategory;
    assignee_id?: string;
    created_by_id?: string;
  } = {}, { rejectWithValue }) => {
    try {
      setAuthHeader();

       // Build query string from filters
       const queryParams = new URLSearchParams();
       Object.entries(filters).forEach(([key, value]) => {
            if (value) {
            queryParams.append(key, value);
            }
       });
      const response = await api.get(`/tickets${queryParams.toString() ? '?' + queryParams.toString() : ''}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch tickets');
    }
  }
);

export const fetchTicket = createAsyncThunk(
  'tickets/fetchTicket',
  async (ticketId: string, { rejectWithValue }) => {
    try {
      setAuthHeader();
      const response = await api.get(`/tickets/${ticketId}?include_alerts=true`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch ticket');
    }
  }
);

export const createTicket = createAsyncThunk(
  'tickets/createTicket',
  async (ticketData: {
    alert_ids: string[];
    title: string;
    description: string;
    client_id: string;
    category?: TicketCategory;
    priority?: TicketPriority;
    assignee_id?: string;
    due_date?: string;
    // risk_score?: number;
    // category_area?: string;
    // technology_id?: string;
    // technology_url?: string;
    // technology_name?: string;
    // deduplication_id?: string;
  }, { rejectWithValue }) => {
    try {
      setAuthHeader();
      const response = await api.post('/tickets', ticketData);
      // Backend returns the ticket object directly (with alert info)
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create ticket');
    }
  }
);

export const addAlertsToTicket = createAsyncThunk(
  'tickets/addAlertsToTicket',
  async ({ ticketId, alertIds }: { ticketId: string; alertIds: string[] }, { rejectWithValue }) => {
    try {
      setAuthHeader();
      const response = await api.post(`/tickets/${ticketId}/alerts`, { alert_ids: alertIds });
      return response.data;
    } catch (error: any) {
      const msg = error.response?.data?.error || error.response?.data?.message || 'Failed to add alerts to ticket';
      return rejectWithValue(msg);
    }
  }
);

export const updateTicket = createAsyncThunk(
  'tickets/updateTicket',
  async ({ ticketId, ticketData }: {
    ticketId: string;
    ticketData: {
      alert_id?: string;
      title?: string;
      description?: string;
      status?: TicketStatus;
      category?: TicketCategory;
      priority?: TicketPriority;
      assignee_id?: string;
      client_id?: string;
      due_date?: string;
      raw_data?: any;
      client_recommendations?: string | null;
    };
  }, { rejectWithValue }) => {
    try {
       setAuthHeader();
       const response = await api.put(`/tickets/${ticketId}`, ticketData);
       return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update ticket');
    }
  }
);

export const removeAlertsFromTicket = createAsyncThunk(
  'tickets/removeAlertsFromTicket',
  async ({ ticketId, alertIds }: { ticketId: string; alertIds: string[] }, { rejectWithValue }) => {
    try {
      setAuthHeader();
      const response = await api.delete(`/tickets/${ticketId}/alerts`, { data: { alert_ids: alertIds } });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove alerts from ticket');
    }
  }
);

export const deleteTicket = createAsyncThunk(
  'tickets/deleteTicket',
  async (ticketId: string, { rejectWithValue }) => {
    try {
      setAuthHeader();
      await api.delete(`/tickets/${ticketId}`);
      // Backend returns success message, but we just need the ticketId for state management
      return ticketId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete ticket');
    }
  }
);

export const resolveTicket = createAsyncThunk(
  'tickets/resolveTicket',
  async ({ ticketId, comment, mitreTags }: { ticketId: string; comment?: string; mitreTags?: string[] }, { rejectWithValue }) => {
    try {
      setAuthHeader();
      const response = await api.post(`/tickets/${ticketId}/resolve`, {
        root_cause: comment,
        comment,
        ...(mitreTags && mitreTags.length ? { mitre_tags: mitreTags } : {}),
      });
      return response.data;
    } catch (error: any) {
      const msg = error.response?.data?.error || error.response?.data?.message || 'Failed to resolve ticket';
      return rejectWithValue(msg);
    }
  }
);

export const escalateTicket = createAsyncThunk(
  'tickets/escalateTicket',
  async ({ ticketId, comment }: { ticketId: string; comment?: string }, { rejectWithValue }) => {
    try {
      setAuthHeader();
      const response = await api.post(`/tickets/${ticketId}/escalate`, { comment });
      return response.data;
    } catch (error: any) {
      const msg = error.response?.data?.error || error.response?.data?.message || 'Failed to escalate ticket';
      return rejectWithValue(msg);
    }
  }
);

export const clientEscalateTicket = createAsyncThunk(
  'tickets/clientEscalateTicket',
  async ({ ticketId, comment }: { ticketId: string; comment?: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/tickets/${ticketId}/request-escalation`, { comment });
      const { escalation_comment, ...ticket } = response.data;
      return { ticket, escalation_comment } as { ticket: Ticket; escalation_comment: TicketComment };
    } catch (error: any) {
      const msg = error.response?.data?.error || error.response?.data?.message || 'Failed to request escalation';
      return rejectWithValue(msg);
    }
  }
);

export const fetchTicketComments = createAsyncThunk(
  'tickets/fetchTicketComments',
  async (ticketId: string, { rejectWithValue }) => {
    try {
      setAuthHeader();
      const response = await api.get(`/tickets/${ticketId}/comments`);
      // Backend returns { comments: [...], count: number }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch comments');
    }
  }
);

export const addTicketComment = createAsyncThunk(
  'tickets/addTicketComment',
  async ({ ticketId, content }: { ticketId: string; content: string }, { rejectWithValue }) => {
    try {
      setAuthHeader();
      const response = await api.post(`/tickets/${ticketId}/comments`, { content });
      // Backend returns the created comment object
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add comment');
    }
  }
);

export const fetchTicketAttachments = createAsyncThunk(
  'tickets/fetchTicketAttachments',
  async (ticketId: string, { rejectWithValue }) => {
    try {
      setAuthHeader();
      const response = await api.get(`/tickets/${ticketId}/attachments`);
      // Backend returns { attachments: [...], count: number }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch attachments');
    }
  }
);

export const fetchTicketActivity = createAsyncThunk(
  'tickets/fetchTicketActivity',
  async (ticketId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/tickets/${ticketId}/activity`);
      return response.data as TicketActivity[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch activity');
    }
  }
);

export const addTicketAttachment = createAsyncThunk(
  'tickets/addTicketAttachment',
  async ({ ticketId, file }: { ticketId: string; file: File }, { rejectWithValue }) => {
    try {
      setAuthHeader();
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/tickets/${ticketId}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Backend returns the created attachment object
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add attachment');
    }
  }
);

export const deleteTicketAttachment = createAsyncThunk(
  'tickets/deleteTicketAttachment',
  async ({ ticketId, attachmentId }: { ticketId: string; attachmentId: string }, { rejectWithValue }) => {
    try {
      setAuthHeader();
      await api.delete(`/tickets/${ticketId}/attachments/${attachmentId}`);
      return attachmentId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete attachment');
    }
  }
);

// Tickets slice
const ticketsSlice = createSlice({
  name: 'tickets',
  initialState,
  reducers: {
    clearSelectedTicket: (state) => {
      state.selectedTicket = null;
      state.comments = [];
      state.attachments = [];
      state.activity = [];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch tickets
    builder.addCase(fetchTickets.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTickets.fulfilled, (state, action: PayloadAction<{ tickets: Ticket[]; count: number }>) => {
      state.isLoading = false;
      state.tickets = action.payload.tickets ?? [];
    });
    builder.addCase(fetchTickets.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Fetch single ticket
    builder.addCase(fetchTicket.pending, (state) => {
      state.isDetailsLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTicket.fulfilled, (state, action: PayloadAction<Ticket>) => {
      state.isDetailsLoading = false;
      state.selectedTicket = action.payload;
    });
    builder.addCase(fetchTicket.rejected, (state, action) => {
      state.isDetailsLoading = false;
      state.error = action.payload as string;
    });

    // Create ticket
    builder.addCase(createTicket.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createTicket.fulfilled, (state, action: PayloadAction<Ticket>) => {
      state.isLoading = false;
      state.tickets.push(action.payload);
    });
    builder.addCase(createTicket.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    // Add alerts to ticket
    builder.addCase(addAlertsToTicket.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(addAlertsToTicket.fulfilled, (state, action: PayloadAction<Ticket>) => {
      state.isLoading = false;
      const index = state.tickets.findIndex(ticket => ticket.id === action.payload.id);
      if (index !== -1) {
        state.tickets[index] = action.payload;
      }
      if (state.selectedTicket?.id === action.payload.id) {
        state.selectedTicket = action.payload;
      }
    });
    builder.addCase(addAlertsToTicket.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Remove alerts from ticket
    builder.addCase(removeAlertsFromTicket.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(removeAlertsFromTicket.fulfilled, (state, action: PayloadAction<Ticket>) => {
      state.isLoading = false;
      const index = state.tickets.findIndex(ticket => ticket.id === action.payload.id);
      if (index !== -1) {
        state.tickets[index] = action.payload;
      }
      if (state.selectedTicket?.id === action.payload.id) {
        state.selectedTicket = action.payload;
      }
    });
    builder.addCase(removeAlertsFromTicket.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Update ticket
    builder.addCase(updateTicket.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateTicket.fulfilled, (state, action: PayloadAction<Ticket>) => {
      state.isLoading = false;
      const index = state.tickets.findIndex(ticket => ticket.id === action.payload.id);
      if (index !== -1) {
        state.tickets[index] = action.payload;
      }
      if (state.selectedTicket?.id === action.payload.id) {
        state.selectedTicket = action.payload;
      }
    });
    builder.addCase(updateTicket.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Delete ticket
    builder.addCase(deleteTicket.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteTicket.fulfilled, (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.tickets = state.tickets.filter(ticket => ticket.id !== action.payload);
      if (state.selectedTicket?.id === action.payload) {
        state.selectedTicket = null;
      }
    });
    builder.addCase(deleteTicket.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Resolve ticket
    builder.addCase(resolveTicket.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(resolveTicket.fulfilled, (state, action: PayloadAction<Ticket>) => {
      state.isLoading = false;
      const index = state.tickets.findIndex(ticket => ticket.id === action.payload.id);
      if (index !== -1) {
        state.tickets[index] = action.payload;
      }
      if (state.selectedTicket?.id === action.payload.id) {
        state.selectedTicket = action.payload;
      }
    });
    builder.addCase(resolveTicket.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Escalate ticket
    builder.addCase(escalateTicket.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(escalateTicket.fulfilled, (state, action: PayloadAction<Ticket>) => {
      state.isLoading = false;
      const index = state.tickets.findIndex(ticket => ticket.id === action.payload.id);
      if (index !== -1) {
        state.tickets[index] = action.payload;
      }
      if (state.selectedTicket?.id === action.payload.id) {
        state.selectedTicket = action.payload;
      }
    });
    builder.addCase(escalateTicket.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Client request escalation (POST /tickets/:id/request-escalation)
    builder.addCase(clientEscalateTicket.pending, (state) => {
      state.error = null;
    });
    builder.addCase(clientEscalateTicket.fulfilled, (state, action: PayloadAction<{ ticket: Ticket; escalation_comment: TicketComment }>) => {
      const { ticket, escalation_comment } = action.payload;
      const index = state.tickets.findIndex(t => t.id === ticket.id);
      if (index !== -1) state.tickets[index] = ticket;
      if (state.selectedTicket?.id === ticket.id) state.selectedTicket = ticket;
      state.comments.push(escalation_comment);
      state.selectedTicket && (state.selectedTicket.comments_count += 1);
    });
    builder.addCase(clientEscalateTicket.rejected, (state, action) => {
      state.error = action.payload as string;
    });

    // Fetch ticket comments
    builder.addCase(fetchTicketComments.pending, (state) => {
      state.error = null;
    });
    builder.addCase(fetchTicketComments.fulfilled, (state, action: PayloadAction<{ comments: TicketComment[]; count: number }>) => {
      state.comments = action.payload.comments;
    });
    builder.addCase(fetchTicketComments.rejected, (state, action) => {
      state.error = action.payload as string;
    });

    // Add ticket comment
    builder.addCase(addTicketComment.fulfilled, (state, action: PayloadAction<TicketComment>) => {
      state.comments.push(action.payload);
      if (state.selectedTicket) {
        state.selectedTicket.comments_count += 1;
      }
    });

    // Fetch ticket attachments
    builder.addCase(fetchTicketAttachments.pending, (state) => {
      state.error = null;
    });
    builder.addCase(fetchTicketAttachments.fulfilled, (state, action: PayloadAction<{ attachments: TicketAttachment[]; count: number }>) => {
      state.attachments = action.payload.attachments;
    });
    builder.addCase(fetchTicketAttachments.rejected, (state, action) => {
      state.error = action.payload as string;
    });

    // Add ticket attachment
    builder.addCase(addTicketAttachment.fulfilled, (state, action: PayloadAction<TicketAttachment>) => {
      state.attachments.push(action.payload);
      if (state.selectedTicket) {
        state.selectedTicket.attachments_count += 1;
      }
    });

    // Delete ticket attachment
    builder.addCase(deleteTicketAttachment.fulfilled, (state, action: PayloadAction<string>) => {
      state.attachments = state.attachments.filter(a => a.id !== action.payload);
      if (state.selectedTicket) {
        state.selectedTicket.attachments_count = Math.max(0, state.selectedTicket.attachments_count - 1);
      }
    });

    // Fetch ticket activity
    builder.addCase(fetchTicketActivity.fulfilled, (state, action: PayloadAction<TicketActivity[]>) => {
      state.activity = action.payload;
    });
  },
});

export const { clearSelectedTicket, clearError } = ticketsSlice.actions;
export default ticketsSlice.reducer;
